import { supabase } from '../supabaseClient';
import { buildPlanValidationContent, sha256Hex } from './journeyState';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHA_RE = /^[0-9a-f]{64}$/;
const CHANNELS = new Set(['email', 'hand_delivery', 'post']);
const REFERENCE_KEYS = [
  'artifact_sha256',
  'client_id',
  'client_form_sha256',
  'confirmed_at',
  'consultation_id',
  'content_sha256',
  'document_channel',
  'revision_sha256',
  'status',
];

function exactKeys(value, keys) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function nullableString(value) {
  return value === null ? null : String(value || '');
}

export function normalizeFollowupDocumentReference(raw) {
  if (!exactKeys(raw, REFERENCE_KEYS)) {
    throw new Error('Preuve de remise document incomplète. Recharge la fiche.');
  }
  const status = String(raw.status || '');
  const clientId = String(raw.client_id || '');
  const consultationId = String(raw.consultation_id || '');
  const contentSha256 = String(raw.content_sha256 || '').toLowerCase();
  const revisionSha256 = String(raw.revision_sha256 || '').toLowerCase();
  const clientFormSha256 = String(raw.client_form_sha256 || '').toLowerCase();
  const artifactSha256 = nullableString(raw.artifact_sha256);
  const documentChannel = nullableString(raw.document_channel);
  const confirmedAt = nullableString(raw.confirmed_at);
  if (!['ready', 'confirmed'].includes(status)
      || !UUID_RE.test(clientId)
      || !UUID_RE.test(consultationId)
      || !SHA_RE.test(contentSha256)
      || !SHA_RE.test(revisionSha256)
      || !SHA_RE.test(clientFormSha256)) {
    throw new Error('Preuve de remise document invalide. Recharge la fiche.');
  }
  if (status === 'ready' && (artifactSha256 !== null || documentChannel !== null || confirmedAt !== null)) {
    throw new Error('État de remise document incohérent. Recharge la fiche.');
  }
  if (status === 'confirmed' && (
    !SHA_RE.test(String(artifactSha256 || ''))
    || !CHANNELS.has(documentChannel)
    || !Number.isFinite(Date.parse(confirmedAt || ''))
  )) {
    throw new Error('Confirmation de remise document invalide. Recharge la fiche.');
  }
  return Object.freeze({
    status,
    clientId,
    consultationId,
    contentSha256,
    revisionSha256,
    clientFormSha256,
    artifactSha256,
    documentChannel,
    confirmedAt,
  });
}

export async function loadFollowupDocumentReference({
  clientId,
  consultationId,
  db = supabase,
} = {}) {
  if (!UUID_RE.test(String(clientId || '')) || !UUID_RE.test(String(consultationId || ''))) {
    throw new Error('Version de suivi introuvable.');
  }
  const { data: referenceRaw, error: referenceError } = await db.rpc(
    'preparer_remise_version_suivi_document',
    { p_client_id: clientId, p_consultation_id: consultationId },
  );
  if (referenceError) throw new Error(referenceError.message || 'Impossible de préparer la remise document.');
  const reference = normalizeFollowupDocumentReference(referenceRaw);
  if (reference.clientId !== clientId || reference.consultationId !== consultationId) {
    throw new Error('La preuve ne correspond pas à cette version de suivi.');
  }
  const { data: row, error: rowError } = await db
    .from('nutrition_consultations')
    .select('*')
    .eq('id', consultationId)
    .eq('client_id', clientId)
    .eq('is_deleted', false)
    .maybeSingle();
  if (rowError || !row) {
    throw new Error(rowError?.message || 'La version cloud à remettre est introuvable.');
  }
  const consultation = {
    ...row,
    clientId: row.client_id,
    nutritionPlan: row.nutrition_plan || '',
    createdAt: row.created_at,
    isFollowup: row.is_followup === true,
    followupData: row.followup_data || null,
    previousConsultationId: row.previous_consultation_id || null,
  };
  const actualContentSha256 = await sha256Hex(buildPlanValidationContent(
    consultation.nutritionPlan,
    consultation.supplements || '',
  ));
  if (actualContentSha256 !== reference.contentSha256
      || consultation.isFollowup !== true
      || !UUID_RE.test(String(consultation.previousConsultationId || ''))) {
    throw new Error('La version cloud ne correspond pas à la preuve de remise.');
  }
  const { data: clientRow, error: clientError } = await db
    .from('clients')
    .select('id, form')
    .eq('id', clientId)
    .maybeSingle();
  if (clientError || !clientRow || clientRow.id !== clientId) {
    throw new Error(clientError?.message || 'La fiche cliente exacte est introuvable.');
  }
  // La fiche et la consultation sont lues entre deux preuves serveur. Si une
  // donnée change pendant cette fenêtre, la seconde preuve diffère et aucun
  // Word n'est généré à partir d'un instant incohérent.
  const { data: finalReferenceRaw, error: finalReferenceError } = await db.rpc(
    'preparer_remise_version_suivi_document',
    { p_client_id: clientId, p_consultation_id: consultationId },
  );
  if (finalReferenceError) {
    throw new Error(finalReferenceError.message || 'La preuve de remise a changé.');
  }
  const finalReference = normalizeFollowupDocumentReference(finalReferenceRaw);
  if (JSON.stringify(finalReference) !== JSON.stringify(reference)) {
    throw new Error('La version ou la fiche cliente a changé pendant la préparation.');
  }
  const clientSnapshot = Object.freeze({
    id: clientId,
    prenom: clientRow.form?.prenom || 'Cliente',
    form: Object.freeze({ ...(clientRow.form || {}) }),
  });
  return Object.freeze({
    reference: finalReference,
    consultation: Object.freeze(consultation),
    clientSnapshot,
  });
}

export async function exportFollowupDocument({
  client,
  loaded,
  exportPlan,
} = {}) {
  const reference = loaded?.reference;
  const consultation = loaded?.consultation;
  const clientSnapshot = loaded?.clientSnapshot;
  if (!reference || !consultation || consultation.id !== reference.consultationId
      || consultation.clientId !== reference.clientId
      || !clientSnapshot || clientSnapshot.id !== reference.clientId
      || client?.id !== reference.clientId
      || reference.status !== 'ready' || typeof exportPlan !== 'function') {
    throw new Error('La version document doit être préparée à nouveau.');
  }
  const actualContentSha256 = await sha256Hex(buildPlanValidationContent(
    consultation.nutritionPlan,
    consultation.supplements || '',
  ));
  if (actualContentSha256 !== reference.contentSha256) {
    throw new Error('Le plan exact ne correspond plus à la preuve de remise.');
  }
  const exportResult = await exportPlan(
    clientSnapshot,
    consultation,
    consultation.nutritionPlan || '',
  );
  const artifactSha256 = String(exportResult?.artifactSha256 || '').trim().toLowerCase();
  if (exportResult?.ok !== true || !SHA_RE.test(artifactSha256)) {
    throw new Error('L’empreinte exacte du document Word est absente.');
  }
  return Object.freeze({
    clientId: reference.clientId,
    consultationId: reference.consultationId,
    contentSha256: reference.contentSha256,
    revisionSha256: reference.revisionSha256,
    clientFormSha256: reference.clientFormSha256,
    artifactSha256,
  });
}

export async function confirmFollowupDocumentDelivery({
  proof,
  documentChannel,
  clinicalConfirmed,
  db = supabase,
} = {}) {
  if (!UUID_RE.test(String(proof?.clientId || ''))
      || !UUID_RE.test(String(proof?.consultationId || ''))
      || !SHA_RE.test(String(proof?.contentSha256 || ''))
      || !SHA_RE.test(String(proof?.revisionSha256 || ''))
      || !SHA_RE.test(String(proof?.clientFormSha256 || ''))
      || !SHA_RE.test(String(proof?.artifactSha256 || ''))
      || !CHANNELS.has(documentChannel)
      || clinicalConfirmed !== true) {
    throw new Error('Relis le document exact, choisis son canal et confirme sa remise.');
  }
  const { data, error } = await db.rpc('confirmer_remise_version_suivi_document', {
    p_client_id: proof.clientId,
    p_consultation_id: proof.consultationId,
    p_content_sha256: proof.contentSha256,
    p_revision_sha256: proof.revisionSha256,
    p_client_form_sha256: proof.clientFormSha256,
    p_artifact_sha256: proof.artifactSha256,
    p_document_channel: documentChannel,
    p_clinical_confirmed: true,
  });
  if (error) throw new Error(error.message || 'La remise document n’a pas été confirmée.');
  const normalized = normalizeFollowupDocumentReference(data);
  if (normalized.status !== 'confirmed'
      || normalized.clientId !== proof.clientId
      || normalized.consultationId !== proof.consultationId
      || normalized.contentSha256 !== proof.contentSha256
      || normalized.revisionSha256 !== proof.revisionSha256
      || normalized.clientFormSha256 !== proof.clientFormSha256
      || normalized.artifactSha256 !== proof.artifactSha256
      || normalized.documentChannel !== documentChannel) {
    throw new Error('La preuve persistée ne correspond pas au document remis.');
  }
  return normalized;
}
